import * as React from 'react';
import { Image, StyleSheet, Text, View, ViewStyle } from 'react-native';

import { newDesignSystemFontStyles, normalizeHeight } from './Styles';
import { getLocalizedTexts } from '../../../Locales';
import { checkIsOsVersionMajorGreaterOrEqual } from '../../../lib/mobileSafariExtension/mobileSafariExtension';
import { VerticalInstructionsVariantContext } from './VerticalInstructions';
import color from '../../../style/color';
import { font } from '../../../style/text';

export type ItemNumberT = 1 | 2 | 3 | 4;

const addressBarIcon = { uri: '/assets/images/icons/mobile-safari-address-bar-icon.svg' };
const logo = { uri: '/assets/images/logos/lightning-rounded.svg' };
const warningIcon = { uri: '/assets/images/icons/inline-warning.svg' };
const numberIcons = {
    1: { uri: '/assets/images/icons/number-1.svg' },
    2: { uri: '/assets/images/icons/number-2.svg' },
    3: { uri: '/assets/images/icons/number-3.svg' },
    4: { uri: '/assets/images/icons/number-4.svg' },
};
const switchOnIcon = { uri: '/assets/images/icons/switch-on.svg' };
const extensionPuzzlePieceIcon = { uri: '/assets/images/icons/extension-puzzle-piece.svg' };

// In the vertical "version B" screens the step index is plain black text on a white background; everywhere else
// it stays the original white-on-black numbered circle SVG. Scoped via context so the control group is untouched.
function InstructionNumber({ itemNumber }: { itemNumber: ItemNumberT }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    if (isVertical) return <Text style={styles.numberVertical}>{itemNumber}</Text>;
    return <Image source={numberIcons[itemNumber]} style={styles.imageInstructionIcon} />;
}

function InstructionRow({
    itemNumber,
    detailsStyle,
    multiline,
    children,
}: {
    itemNumber: ItemNumberT;
    detailsStyle?: ViewStyle;
    multiline?: boolean;
    children: React.ReactNode;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const shouldWrap = isVertical || multiline;
    return (
        <View
            style={[
                styles.containerInstruction,
                isVertical && styles.containerInstructionVertical,
                multiline && styles.containerInstructionMultiline,
            ]}
        >
            {multiline ? (
                // The row top-aligns its children so the text can wrap; this box keeps the number
                // vertically centered against the first line instead of pinned to the very top.
                <View style={styles.containerInstructionNumberMultiline}>
                    <InstructionNumber itemNumber={itemNumber} />
                </View>
            ) : (
                <InstructionNumber itemNumber={itemNumber} />
            )}
            <View
                style={[
                    styles.containerInstructionsDetails,
                    shouldWrap && styles.containerInstructionsDetailsVertical,
                    detailsStyle,
                ]}
            >
                {children}
            </View>
        </View>
    );
}

function instructionBodyTextStyle(isVertical: boolean, newCopy?: boolean) {
    if (isVertical) return newDesignSystemFontStyles.paragraphMedium;
    return newCopy ? newDesignSystemFontStyles.newCopyInstructionText : newDesignSystemFontStyles.paragraphSmall;
}

export function InstructionClickSafariSettings({
    itemNumber,
    newCopy,
}: {
    itemNumber: ItemNumberT;
    newCopy?: boolean;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.instructionsClickSafariSettings;
    const textStyle = instructionBodyTextStyle(isVertical, newCopy);
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={textStyle}>{texts.part1}</Text>
            <AddressBarSymbol newCopy={newCopy} />
            <Text style={textStyle}>{texts.part2}</Text>
        </InstructionRow>
    );
}

function AddressBarSymbol({ newCopy }: { newCopy?: boolean }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    if (checkIsOsVersionMajorGreaterOrEqual(18)) {
        const icon = <Image source={addressBarIcon} style={[styles.imageInline, isVertical && styles.imageInlineVertical]} />;
        if (newCopy) return <View style={styles.containerInstructionsDetail}>{icon}</View>;
        return icon;
    }
    return (
        <View style={styles.containerInstructionsDetail}>
            <Text>
                <Text style={styles.textContainerInstructionSmall}>A</Text>
                <Text style={styles.textContainerInstruction}>A</Text>
            </Text>
        </View>
    );
}

export function InstructionManageExtensions({ itemNumber, newCopy }: { itemNumber: ItemNumberT; newCopy?: boolean }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.activateStep.instructionsManageExtensions;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${texts.part2}`}</Text>
            ) : (
                <>
                    <Text style={instructionBodyTextStyle(isVertical, newCopy)}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
                    </View>
                    <Image source={extensionPuzzlePieceIcon} style={styles.imageExtensionPuzzlePieceIcon} />
                </>
            )}
        </InstructionRow>
    );
}

export function InstructionActivateJokoExtension({
    itemNumber,
    newCopy,
}: {
    itemNumber: ItemNumberT;
    newCopy?: boolean;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const text =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.activateStep.instructionsActivateJokoExtension;
    if (isVertical)
        return (
            <InstructionRow itemNumber={itemNumber}>
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{text}</Text>
            </InstructionRow>
        );
    return (
        <InstructionRow itemNumber={itemNumber} detailsStyle={{ gap: 8 } as ViewStyle}>
            <Text style={instructionBodyTextStyle(isVertical, newCopy)}>{text}</Text>
            <View style={styles.containerInstructionIcons}>
                <Image source={logo} style={styles.imageInline} />
                <Image source={switchOnIcon} style={styles.imageSwitchOnIcon} />
            </View>
        </InstructionRow>
    );
}

export function InstructionClickBanner({ itemNumber }: { itemNumber: ItemNumberT }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsClickBanner;
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text>
                <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part1}</Text>
                <Text style={newDesignSystemFontStyles.paragraphSmallBold}>{texts.part2}</Text>
            </Text>
            <Text style={newDesignSystemFontStyles.paragraphSmall}>{texts.part3}</Text>
        </InstructionRow>
    );
}

export function InstructionClickExtensionWarningIcon({
    itemNumber,
    newCopy,
}: {
    itemNumber: ItemNumberT;
    newCopy?: boolean;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep
            .instructionsClickExtensionWarningIcon;
    const textStyle = instructionBodyTextStyle(isVertical, newCopy);
    return (
        <InstructionRow itemNumber={itemNumber}>
            <Text style={textStyle}>{texts.part1}</Text>
            <Image source={warningIcon} style={[styles.warningInline, isVertical && styles.warningInlineVertical]} />
            <Text style={textStyle}>{texts.part2}</Text>
        </InstructionRow>
    );
}

export function InstructionAllowAlways({ itemNumber, newCopy }: { itemNumber: ItemNumberT; newCopy?: boolean }) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsAlwaysAllow;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${texts.part2}`}</Text>
            ) : (
                <>
                    <Text style={instructionBodyTextStyle(isVertical, newCopy)}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{texts.part2}</Text>
                    </View>
                </>
            )}
        </InstructionRow>
    );
}

export function InstructionAllowOnAllWebsites({
    itemNumber,
    newCopy,
}: {
    itemNumber: ItemNumberT;
    newCopy?: boolean;
}) {
    const isVertical = React.useContext(VerticalInstructionsVariantContext);
    const texts =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.authorizeStep.instructionsAllowOnAllWebsites;
    const part2 = newCopy ? texts.newCopyPart2 : texts.part2;
    return (
        <InstructionRow itemNumber={itemNumber}>
            {isVertical ? (
                <Text style={newDesignSystemFontStyles.paragraphMedium}>{`${texts.part1} ${part2}`}</Text>
            ) : (
                <>
                    <Text style={instructionBodyTextStyle(isVertical, newCopy)}>{texts.part1}</Text>
                    <View style={styles.containerInstructionsDetail}>
                        <Text style={styles.textContainerInstruction}>{part2}</Text>
                    </View>
                </>
            )}
        </InstructionRow>
    );
}

// A bordered chip echoing an iOS Settings label (e.g. "Extensions", "Autoriser").
function SettingsLabelChip({ label }: { label: string }) {
    return (
        <View style={styles.containerInstructionsDetail}>
            <Text style={styles.textContainerInstruction}>{label}</Text>
        </View>
    );
}

// Recovery steps shown after the user fails to grant the all-websites permission. Both iOS variants are
// four steps that differ only in step 1; steps 2–4 (tap Joko → Other websites → Allow) are identical.
//
// When `deepLinksToExtensions` (iOS 26.2+), the "Open Settings" deep link lands the user inside Safari →
// Extensions — Joko's page directly on a warm Settings, the extensions list on a cold one (iOS can't
// deep-link past the list reliably), so step 2 "tap Joko" still applies — and step 1 is just "Open
// Settings". Otherwise (iOS < 26.2) the deep link lands on the Safari pane, so step 1 walks the user to
// the Extensions row first. See GRW-253 / GRW-281.
//
// Only step 1 can wrap onto two lines, so it alone is `multiline`; steps 2–4 stay single-line with the
// number vertically centered against their content (matching the Figma layout).
export function MissingAuthorizationInstructions({ deepLinksToExtensions }: { deepLinksToExtensions: boolean }) {
    const { settingsLabels, openSettings, findExtensions, tapJoko, tapAllWebsites, tapAuthorize } =
        getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.missingAuthorization;
    return (
        <>
            {deepLinksToExtensions ? (
                <InstructionRow itemNumber={1}>
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{openSettings}</Text>
                </InstructionRow>
            ) : (
                <InstructionRow itemNumber={1} multiline>
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{findExtensions.part1}</Text>
                    <SettingsLabelChip label={settingsLabels.extensions} />
                    <Text style={newDesignSystemFontStyles.paragraphSmall}>{findExtensions.part2}</Text>
                </InstructionRow>
            )}
            <InstructionRow itemNumber={2}>
                <Text style={newDesignSystemFontStyles.paragraphSmall}>{tapJoko}</Text>
                <Image source={logo} style={styles.imageJokoLogo} />
            </InstructionRow>
            <InstructionRow itemNumber={3}>
                <Text style={newDesignSystemFontStyles.paragraphSmall}>{tapAllWebsites}</Text>
                <SettingsLabelChip label={settingsLabels.allWebsites} />
            </InstructionRow>
            <InstructionRow itemNumber={4}>
                <Text style={newDesignSystemFontStyles.paragraphSmall}>{tapAuthorize}</Text>
                <SettingsLabelChip label={settingsLabels.authorize} />
            </InstructionRow>
        </>
    );
}

const styles = StyleSheet.create({
    containerInstruction: {
        height: normalizeHeight(25),
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
        alignSelf: 'stretch',
    },
    // Vertical "version B" steps can wrap onto two lines, so the fixed single-line height is dropped. The number
    // is vertically centered against the (possibly multi-line) text. The row gap matches the container's
    // horizontal padding (16) and the number is center-aligned in its fixed-width box, so the space on each side
    // of the number (to the border and to the text) is balanced.
    containerInstructionVertical: {
        height: undefined,
        alignItems: 'center',
        gap: 16,
    },
    // A recovery step whose text wraps onto two lines (e.g. "Open Settings, search [Extensions] and tap
    // on it") must let the words flow instead of squeezing into a stacked column. Drops the fixed
    // single-line height and top-aligns the row's children so wrapping starts at the first line; the
    // number is re-centered against that first line via containerInstructionNumberMultiline. See GRW-253.
    containerInstructionMultiline: {
        height: undefined,
        alignItems: 'flex-start',
    },
    // Keeps the step number vertically centered against the first line of a wrapping (multiline) step:
    // the box is one line tall and centers the number, mirroring the Figma layout where the number is
    // boxed to the first line's height rather than pinned to the very top of the multi-line block.
    containerInstructionNumberMultiline: {
        height: normalizeHeight(25),
        justifyContent: 'center',
    },
    containerInstructionsDetailsVertical: {
        flexWrap: 'wrap',
    },
    numberVertical: {
        width: normalizeHeight(22),
        fontFamily: font.ambitBold,
        fontSize: 20,
        lineHeight: 20 * 1.2,
        textAlign: 'center',
        color: color.black,
    },
    imageInlineVertical: {
        width: normalizeHeight(13),
        height: normalizeHeight(13),
    },
    warningInlineVertical: {
        width: normalizeHeight(15),
        height: normalizeHeight(15),
    },
    containerInstructionsDetails: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
    },
    containerInstructionsDetail: {
        flexDirection: 'row',
        paddingHorizontal: 8,
        paddingVertical: 3,
        alignItems: 'center',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(0, 0, 0, 0.8)',
    },
    containerInstructionIcons: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 4,
    },
    textContainerInstruction: {
        fontWeight: 'bold',
        fontSize: 12,
        lineHeight: 14 * 1.4,
        color: 'rgba(0, 0, 0, 0.8)',
    },
    textContainerInstructionSmall: {
        fontWeight: 'bold',
        fontSize: 10,
        lineHeight: 14 * 1.4,
        color: 'rgba(0, 0, 0, 0.8)',
    },
    imageInstructionIcon: {
        width: normalizeHeight(20),
        height: normalizeHeight(20),
    },
    imageInline: {
        width: normalizeHeight(16),
        height: normalizeHeight(16),
    },
    // The Joko app icon next to "Tap on Joko" on the recovery screen; 24px per the Figma design.
    imageJokoLogo: {
        width: normalizeHeight(24),
        height: normalizeHeight(24),
    },
    warningInline: {
        width: normalizeHeight(20),
        height: normalizeHeight(20),
    },
    imageSwitchOnIcon: {
        height: normalizeHeight(16),
        width: normalizeHeight(26.323),
        resizeMode: 'stretch',
    },
    imageExtensionPuzzlePieceIcon: {
        height: normalizeHeight(20),
        width: normalizeHeight(20),
    },
});
